import * as React from 'react';
import { styled } from '@glitz/react';
import { Block, desktopEdgeMarginDecorator, contentWidthDecorator } from 'webapp/components/page-layout';
import Image from 'webapp/components/image';
import { Link, ProgressiveHydration, registerComponent, useMatchMobile } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { mapContentHorizontalAlignment, mapContentVerticalAlignment } from 'webapp/shared/content-alignment';
import { ButtonBlockGroup } from 'webapp/src/components/ButtonBlockGroup';
import { decorators } from 'webapp/html-decorators/fontSizeDecorator';
import { useViewEventTrigger } from 'webapp/tracking';
import { addSelectPromotionToDataLayer, addViewPromotionToDataLayer } from 'webapp/tracking/events/promotions';
import { useNinetailedClickEvent } from '@avensia/excite-ninetailed';
import { linkWithoutQueryParams } from 'webapp/link-url-decorator';
import Countdown from 'webapp/shared/Countdown';
import { LayoutProvider, useLayout } from 'webapp/shared/use-layout';
import { useTrackingBlock } from '@avensia/excite-ninetailed';

export default registerComponent<NordicNest.Api.NNImageBlock>(
  React.memo(props => {
    const context = useLayout();
    return (
      <ProgressiveHydration>
        <LayoutProvider {...context} index={(props as any).index}>
          <ImageBlock {...props} />
        </LayoutProvider>
      </ProgressiveHydration>
    );
  }),
);

function ImageBlock(props: NordicNest.Api.NNImageBlock) {
  const isMobile = useMatchMobile();
  const image = isMobile ? props.smallViewportImage || props.largeViewportImage : props.largeViewportImage;
  const { hasLayoutBlockParent } = useLayout();
  const self = React.useRef<HTMLDivElement>(null);

  useViewEventTrigger(self, addViewPromotionToDataLayer, {
    content: props,
  });
  useTrackingBlock(self);

  const ninetailedClickEvent = useNinetailedClickEvent();

  const marginDecorator = React.useMemo(
    () =>
      styled(
        createMaxSmallViewportDecorator({ marginBottom: hasLayoutBlockParent ? 0 : t => t.spacing.l }),
        {
          marginBottom: hasLayoutBlockParent ? 0 : t => t.spacing.xl,
        },
        hasLayoutBlockParent ? {} : desktopEdgeMarginDecorator,
      ),
    [hasLayoutBlockParent],
  );
  const contentDecorator = React.useMemo(
    () =>
      styled({
        justifyContent: mapContentVerticalAlignment(props.contentVerticalAlignment),
        alignItems: mapContentHorizontalAlignment(props.contentHorizontalAlignment),
        textAlign: props.contentHorizontalAlignment === 'center' ? 'center' : undefined,
        backgroundImage:
          props.contentVerticalAlignment === 'bottom'
            ? `linear-gradient(0deg,rgba(0,0,0, ${props.gradientOpacity}) 0,transparent 50%,transparent)`
            : props.contentVerticalAlignment === 'top'
              ? `linear-gradient(180deg,rgba(0,0,0, ${props.gradientOpacity}) 0,transparent 50%,transparent)`
              : `linear-gradient(0deg, transparent 0%, rgba(0,0,0,${props.gradientOpacity}) 50%, transparent 100%)`,
      }),
    [props.contentHorizontalAlignment, props.contentVerticalAlignment, props.gradientOpacity],
  );

  const args: { [placeholder: string]: React.ReactNode } = React.useMemo(
    () => ({
      ...(props.countdown ? { countdown: <Countdown countdown={props.countdown} /> } : {}),
      ...(props.buttons ? { buttons: <ButtonBlockGroup buttons={props.buttons} /> } : {}),
    }),
    [props.countdown, props.buttons],
  );

  const content = (
    <ContentWrapper>
      <Media image={image} />
      <Content css={contentDecorator}>
        <Document styleDecorators={styles} decorators={decorators} elements={props.content} args={args} />
      </Content>
    </ContentWrapper>
  );

  return (
    <Base
      id={props.id}
      css={styled(!hasLayoutBlockParent && contentWidthDecorator('normal'), marginDecorator)}
      ref={self}
    >
      {props.link ? (
        <Link
          {...linkWithoutQueryParams(props.link)}
          onClick={() => {
            addSelectPromotionToDataLayer({ content: props });
            ninetailedClickEvent();
          }}
        >
          {content}
        </Link>
      ) : (
        content
      )}
      {props.bottomContentTinyMce?.length > 0 ? (
        <BottomContent>
          <Document elements={props.bottomContentTinyMce} styleDecorators={bottomContentStyles} />
        </BottomContent>
      ) : (
        props.bottomContent && (
          <BottomContent>
            <Document {...props.bottomContent} styleDecorators={bottomContentStyles} />
          </BottomContent>
        )
      )}
    </Base>
  );
}

const styles: StyleDecorators = {
  h1: styled(createMinLargeViewportDecorator({ fontSize: t => t.fontSize.xxxl, marginBottom: 0 }), {
    fontSize: t => t.fontSize.xl,
    marginBottom: 0,
  }),
  h2: styled(createMinLargeViewportDecorator({ fontSize: t => t.fontSize.xl, marginBottom: 0 }), {
    fontSize: t => t.fontSize.l,
    marginBottom: 0,
  }),
  h3: styled(createMinLargeViewportDecorator({ fontSize: t => t.fontSize.l, marginBottom: 0 }), {
    fontSize: t => t.fontSize.m,
    marginBottom: 0,
  }),
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
    lineHeight: 'normal',
  }),
};

const bottomContentStyles: StyleDecorators = {
  ...styles,
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    fontSize: t => t.fontSize.xxs,
    marginBottom: 0,
  }),
};

const Base = styled(Block, {
  position: 'relative',
  overflow: 'hidden',
});

const Media = styled(Image, {
  width: '100%',
});

const BottomContent = styled.div(createMaxSmallViewportDecorator({ margin: { x: t => t.spacing.m } }), {
  marginTop: t => t.spacing.m,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: t => t.dark.color,
  fontSize: t => t.fontSize.xxs,
});

const ContentWrapper = styled.div({
  position: 'relative',
});

const Content = styled.div(
  createMaxSmallViewportDecorator({
    padding: { xy: t => t.spacing.m },
    fontSize: t => t.fontSize.xxs,
  }),
  {
    padding: { xy: t => t.spacing.l },
    display: 'flex',
    flexDirection: 'column',
    color: 'white',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
);
